/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useApiKeysList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing API Keys list data and DataGrid state.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchApiKeys } from '../api';
import { useApiKeysColumns } from './useApiKeysColumns';

export function useApiKeysList() {
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || t('apiKeys.defaultTenant');
  const availableColumns = useApiKeysColumns();

  const fetchApiKeysData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const response = await fetchApiKeys(params, tenantSlug, user?.id);
        return {
          data: response.data || [],
          pagination: response.pagination || {
            page: params.page,
            limit: params.limit,
            total: response.total || 0,
            totalPages: response.pagination?.totalPages || 0
          }
        } as DataGridFetchResult;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('apiKeys.errors.loadFailed', 'Failed to load API keys');
        showError(message);
        throw error;
      }
    },
    [tenantSlug, user?.id, showError, t],
  );

  return useDataGrid({
    fetchData: fetchApiKeysData,
    initialColumns: availableColumns,
    initialSortBy: 'created_at',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'apiKeys',
  });
}

